<!--
  AI:
  Status: "in progress"
  Type: Component
  SubType: Display
  Reason: Template of bm-customer-account-bank-connections - the four sections of the account tab
          „Bankverbindungen & IBANs" (round 11, WP-B/B1): Zeitscheiben, Für Lastschriften verwendet,
          Zahler-IBANs eingehender Überweisungen (moved unchanged from the full view), Meldungen
          (WidgetFactory table). Full IBAN (Q-11c). Every section: loading (skeletons), error
          (p-message + „Erneut laden"), empty (guidance), ready.
-->
@if (initialized) {
    <div class="flex flex-column gap-4">

        <!-- ================= (1) Zeitscheiben ================= -->
        <section class="surface-card border-round-lg border-1 surface-border p-3">
            <div class="flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
                <div class="flex align-items-center gap-2">
                    <i class="fa-regular fa-building-columns text-lg text-600"></i>
                    <span class="text-lg font-semibold">Zeitscheiben der Bankverbindung</span>
                    @if (bankSlices.length) {
                        <p-badge [value]="bankSlices.length.toString()" severity="secondary"></p-badge>
                    }
                </div>
                <!--
                  The tooltip sits on a wrapping span ON PURPOSE: a disabled p-button carries
                  pointer-events: none and would swallow the hover that explains WHY it is disabled.
                -->
                <span [pTooltip]="getBankChangeWizardTooltip()" tooltipPosition="left">
                    <p-button label="Bankverbindung ersetzen (Assistent)"
                              icon="fa-regular fa-right-left"
                              severity="secondary"
                              [outlined]="true"
                              size="small"
                              [disabled]="!canOpenBankChangeWizard()"
                              (onClick)="openBankChangeWizard()"></p-button>
                </span>
            </div>
            <p class="text-sm text-500 mt-0 mb-3">
                Konfigurierte Bankverbindung je Zeitraum – maßgeblich für den SEPA-Einzug ist die zum Laufbeginn gültige Scheibe.
            </p>

            @if (loading) {
                @for (row of skeletonRows; track row) {
                    <div class="flex align-items-center gap-3 p-2">
                        <p-skeleton shape="circle" size="2.5rem"></p-skeleton>
                        <div class="flex flex-column gap-2 flex-1">
                            <p-skeleton width="16rem" height="1rem"></p-skeleton>
                            <p-skeleton width="24rem" height="0.75rem"></p-skeleton>
                        </div>
                    </div>
                }
            } @else if (loadError) {
                <p-message severity="error" icon="fa-regular fa-triangle-exclamation" styleClass="w-full">
                    <div class="flex flex-wrap align-items-center justify-content-between gap-3 w-full">
                        <span class="text-sm">Die Bankverbindungen dieses Abrechnungskontos konnten nicht geladen werden.</span>
                        <p-button label="Erneut laden" icon="fa-regular fa-rotate-right" severity="secondary"
                                  [outlined]="true" size="small" (onClick)="reload()"></p-button>
                    </div>
                </p-message>
            } @else {

                @if (showMissingBankWarning()) {
                    <p-message severity="warn" icon="fa-regular fa-triangle-exclamation" styleClass="w-full mb-3">
                        <div class="flex flex-wrap align-items-center justify-content-between gap-3 w-full">
                            <span class="text-sm">
                                <span class="font-semibold">Keine Bankverbindung hinterlegt</span> – der SEPA-Schritt des nächsten Abrechnungslaufs bricht für dieses Konto ab.
                            </span>
                            <span [pTooltip]="getBankChangeWizardTooltip()" tooltipPosition="left">
                                <p-button label="Jetzt hinterlegen" icon="fa-regular fa-building-columns" size="small"
                                          [disabled]="!canOpenBankChangeWizard()"
                                          (onClick)="openBankChangeWizard()"></p-button>
                            </span>
                        </div>
                    </p-message>
                }

                @if (showNoBankRequiredHint()) {
                    <p-message severity="info" icon="fa-regular fa-circle-info" styleClass="w-full">
                        <span class="text-sm">Keine Bankverbindung erforderlich (Zahlungsart {{ getPaymentMethodLabel() }}).</span>
                    </p-message>
                }

                @if (sliceRows.length) {
                    <div class="border-round-lg border-1 surface-border overflow-hidden">
                        <p-table [value]="sliceRows" [scrollable]="true" styleClass="p-datatable-sm" dataKey="slice.sliceId">
                            <ng-template pTemplate="header">
                                <tr class="bg-gray-50">
                                    <th class="font-bold text-xs uppercase text-500">Bank</th>
                                    <th class="font-bold text-xs uppercase text-500">IBAN</th>
                                    <th class="font-bold text-xs uppercase text-500">Kontoinhaber</th>
                                    <th class="font-bold text-xs uppercase text-500">Mandat</th>
                                    <th class="font-bold text-xs uppercase text-500 w-8rem">Gültig von</th>
                                    <th class="font-bold text-xs uppercase text-500 w-8rem">Gültig bis</th>
                                    <th class="font-bold text-xs uppercase text-500 w-10rem">Status</th>
                                </tr>
                            </ng-template>
                            <ng-template pTemplate="body" let-row>
                                <!--
                                  The whole row opens the bank account (generic object dialog, update mode
                                  -> the cr change OBC with badge, usage and the wizard button). Keyboard
                                  reachable through role/tabindex/Enter.
                                -->
                                <tr class="cursor-pointer hover:surface-hover"
                                    [class.text-500]="row.slice.state === 'ended'"
                                    role="link"
                                    tabindex="0"
                                    [attr.aria-label]="'Bankverbindung ' + (row.slice.iban || '') + ' öffnen'"
                                    (click)="openBankAccount(row.slice, $event)"
                                    (keydown.enter)="openBankAccount(row.slice)">
                                    <td>
                                        <bm-bank-badge [bankImage]="row.slice.bankImage"
                                                       [bankName]="row.slice.bankName"
                                                       [branchName]="row.slice.bankBranchName"
                                                       [size]="32"></bm-bank-badge>
                                    </td>
                                    <td class="font-mono font-medium">{{ row.slice.iban || '-' }}</td>
                                    <td>
                                        {{ row.slice.accountHolderName || '-' }}
                                        @if (row.slice.deviantAccountHolder === true) {
                                            <p-tag severity="warn" value="abweichend" styleClass="text-xs ml-1"></p-tag>
                                        }
                                    </td>
                                    <td>
                                        @if (row.mandateStatusLabel) {
                                            <p-tag severity="secondary" [value]="row.mandateStatusLabel" styleClass="text-xs"></p-tag>
                                        }
                                        @if (row.slice.sepaMandateIdentifier) {
                                            <span class="text-sm text-600 ml-1">{{ row.slice.sepaMandateIdentifier }}</span>
                                        }
                                        @if (!row.mandateStatusLabel && !row.slice.sepaMandateIdentifier) {
                                            -
                                        }
                                    </td>
                                    <td>{{ row.slice.startDate | date:'dd.MM.yyyy' }}</td>
                                    <td>
                                        @if (row.slice.endDateForDisplay) {
                                            {{ row.slice.endDateForDisplay | date:'dd.MM.yyyy' }}
                                        } @else {
                                            <span class="text-500">unbefristet</span>
                                        }
                                    </td>
                                    <td>
                                        <p-tag [severity]="row.stateSeverity" [value]="row.stateLabel" styleClass="text-xs font-bold"></p-tag>
                                        @if (row.bankEntityStatusLabel) {
                                            <p-tag severity="secondary" [value]="row.bankEntityStatusLabel" styleClass="text-xs ml-1"></p-tag>
                                        }
                                    </td>
                                </tr>
                            </ng-template>
                        </p-table>
                    </div>
                }
                <!--
                  No separate empty state here ON PURPOSE: with zero slices there is no current slice
                  either, so exactly ONE of the two E-9 blocks above always renders (warning for a
                  direct-debit account, neutral hint otherwise) - a third branch would be unreachable.
                -->
            }
        </section>

        <!-- ================= (2) Für Lastschriften verwendet ================= -->
        <section class="surface-card border-round-lg border-1 surface-border p-3">
            <div class="flex flex-wrap align-items-center justify-content-between gap-3 mb-1">
                <div class="flex align-items-center gap-2">
                    <i class="fa-regular fa-file-invoice text-lg text-600"></i>
                    <span class="text-lg font-semibold">Für Lastschriften verwendet</span>
                    @if (sepaDebitRows.length) {
                        <p-badge [value]="sepaDebitRows.length.toString()" severity="secondary"></p-badge>
                    }
                </div>
            </div>
            <p class="text-sm text-500 mt-0 mb-3">
                Einzugs-IBANs aus den SEPA-Schnappschüssen der Läufe – Schnappschuss zum Zeitpunkt des Laufs, wird nie umgeschrieben.
            </p>

            @if (loading) {
                @for (row of skeletonRows; track row) {
                    <div class="flex align-items-center gap-3 p-2">
                        <p-skeleton shape="circle" size="2.5rem"></p-skeleton>
                        <div class="flex flex-column gap-2 flex-1">
                            <p-skeleton width="16rem" height="1rem"></p-skeleton>
                            <p-skeleton width="24rem" height="0.75rem"></p-skeleton>
                        </div>
                    </div>
                }
            } @else if (loadError) {
                <p-message severity="error" icon="fa-regular fa-triangle-exclamation" styleClass="w-full">
                    <div class="flex flex-wrap align-items-center justify-content-between gap-3 w-full">
                        <span class="text-sm">Die Einzugs-IBANs dieses Abrechnungskontos konnten nicht geladen werden.</span>
                        <p-button label="Erneut laden" icon="fa-regular fa-rotate-right" severity="secondary"
                                  [outlined]="true" size="small" (onClick)="reload()"></p-button>
                    </div>
                </p-message>
            } @else if (sepaDebitRows.length) {
                <div class="border-round-lg border-1 surface-border overflow-hidden">
                    <p-table [value]="sepaDebitRows" [scrollable]="true" styleClass="p-datatable-sm">
                        <ng-template pTemplate="header">
                            <tr class="bg-gray-50">
                                <th class="font-bold text-xs uppercase text-500">Bank</th>
                                <th class="font-bold text-xs uppercase text-500">IBAN</th>
                                <th class="font-bold text-xs uppercase text-500">Kontoinhaber</th>
                                <th class="font-bold text-xs uppercase text-500">Mandatsreferenz</th>
                                <th class="font-bold text-xs uppercase text-500 w-8rem">Erster Einzug</th>
                                <th class="font-bold text-xs uppercase text-500 w-8rem">Letzter Einzug</th>
                                <th class="font-bold text-xs uppercase text-500 w-6rem text-right">Einzüge</th>
                                <th class="font-bold text-xs uppercase text-500">Läufe</th>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-row>
                            <tr class="hover:surface-hover">
                                <td>
                                    <bm-bank-badge [bankImage]="row.bankImage"
                                                   [bankName]="row.bankName"
                                                   [branchName]="row.bankBranchName"
                                                   [size]="32"></bm-bank-badge>
                                </td>
                                <td class="font-mono font-medium">
                                    @if (row.group.iban) {
                                        {{ row.group.iban }}
                                    } @else {
                                        <span class="text-500">ohne IBAN</span>
                                    }
                                </td>
                                <td>{{ row.group.accountHolderName || '-' }}</td>
                                <td class="text-600">{{ row.group.mandateIdentifier || '-' }}</td>
                                <td>{{ row.group.firstDebitDate | date:'dd.MM.yyyy' }}</td>
                                <td>{{ row.group.lastDebitDate | date:'dd.MM.yyyy' }}</td>
                                <td class="text-right font-medium">{{ row.group.debitCount }}</td>
                                <td class="text-600" [pTooltip]="row.runsTooltip" tooltipPosition="top">{{ row.runsText || '-' }}</td>
                            </tr>
                        </ng-template>
                    </p-table>
                </div>
                @if (sepaDebitPageFull) {
                    <p class="text-xs text-500 mt-2 mb-0">
                        <i class="fa-regular fa-circle-info mr-1"></i>
                        Nur die letzten {{ sepaDebitPageSize }} Einzüge dieses Kontos sind berücksichtigt.
                    </p>
                }
            } @else {
                <div class="text-center p-4 text-500 surface-100 border-round-lg">
                    <i class="fa-regular fa-file-invoice text-3xl mb-2 block text-300"></i>
                    <p class="m-0">Noch keine Lastschrift erzeugt – Einzugs-IBANs erscheinen hier nach dem ersten SEPA-Lauf dieses Kontos.</p>
                </div>
            }
        </section>

        <!-- ================= (3) Zahler-IBANs eingehender Überweisungen ================= -->
        <section class="surface-card border-round-lg border-1 surface-border p-3">
            <div class="flex flex-wrap align-items-center justify-content-between gap-3 mb-1">
                <div class="flex align-items-center gap-2">
                    <i class="fa-regular fa-money-bill-transfer text-lg text-600"></i>
                    <span class="text-lg font-semibold">Zahler-IBANs eingehender Überweisungen</span>
                    @if (ibanHistory?.length) {
                        <p-badge [value]="ibanHistory.length.toString()" severity="secondary"></p-badge>
                    }
                </div>
            </div>
            <p class="text-sm text-500 mt-0 mb-3">
                IBANs, von denen Zahlungen auf dieses Abrechnungskonto eingegangen sind.
            </p>

            <!--
              The former „IBAN-Verlauf" table of the full view, moved here (WP-B/B1 (3)) - columns and
              rows unchanged; the scroll height is the same 25rem in rem instead of px (no px units).
            -->
            @if (ibanHistory?.length) {
                <div class="border-round-lg border-1 surface-border overflow-hidden">
                    <p-table
                        [value]="ibanHistory"
                        [scrollable]="true"
                        scrollHeight="25rem"
                        styleClass="p-datatable-sm">
                        <ng-template pTemplate="header">
                            <tr class="bg-gray-50">
                                <th class="font-bold text-xs uppercase text-500">IBAN</th>
                                <th class="font-bold text-xs uppercase text-500">Zahlungsempfänger</th>
                                <th class="font-bold text-xs uppercase text-500">Datum</th>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-history>
                            <tr class="hover:surface-hover">
                                <td class="font-mono font-medium">{{ history.iban }}</td>
                                <td class="text-600">{{ history.payeeName || '-' }}</td>
                                <td class="text-600">{{ history.createdDate | date:'dd.MM.yyyy' }}</td>
                            </tr>
                        </ng-template>
                    </p-table>
                </div>
            } @else {
                <div class="text-center p-4 text-500 surface-100 border-round-lg">
                    <i class="fa-regular fa-inbox text-3xl mb-2 block text-300"></i>
                    <p class="m-0">Kein IBAN-Verlauf verfügbar</p>
                    <p class="m-0 text-sm">Es sind noch keine Überweisungen mit Zahler-IBAN auf dieses Abrechnungskonto eingegangen.</p>
                </div>
            }
        </section>

        <!-- ================= (4) Meldungen ================= -->
        <!-- #messagesSection: the scroll target of an E-4 deep link („n SEPA-Meldungen" on a card); focused = primary border. -->
        <section #messagesSection
                 class="surface-card border-round-lg border-1 p-3"
                 [class.border-primary]="isSectionFocused('messages')"
                 [class.surface-border]="!isSectionFocused('messages')">
            <div class="flex flex-wrap align-items-center justify-content-between gap-3 mb-1">
                <div class="flex align-items-center gap-2">
                    <i class="fa-regular fa-bell text-lg text-600"></i>
                    <span class="text-lg font-semibold">Meldungen</span>
                    @if (!loading && !loadError && currentErrorMessageCount > 0) {
                        <p-tag severity="danger" [value]="currentErrorMessageCount + (currentErrorMessageCount === 1 ? ' aktuelle Fehlermeldung' : ' aktuelle Fehlermeldungen')"
                               styleClass="text-xs font-bold"></p-tag>
                    }
                </div>
                <!--
                  (onChange) rebuilds the widget under the other alias. Not redundant next to
                  [(ngModel)]: PrimeNG writes the model before it emits onChange, so the flag already
                  holds the new value here (same ordering as the contracts toggle of the full view).
                -->
                <div class="flex align-items-center gap-2">
                    <p-toggleSwitch [(ngModel)]="includeObsoleteMessages"
                                    (onChange)="handleIncludeObsoleteChange()"></p-toggleSwitch>
                    <span class="text-sm text-600">Veraltete anzeigen</span>
                </div>
            </div>
            <p class="text-sm text-500 mt-0 mb-3">
                Meldungen der SEPA-Vorabprüfung und weiterer Kontoprüfungen der Abrechnungsläufe. Veraltete Meldungen wurden durch eine Wiederholung des Prüfschritts abgelöst und bleiben zur Nachvollziehbarkeit erhalten.
            </p>

            @if (loadError) {
                <p-message severity="warn" icon="fa-regular fa-triangle-exclamation" styleClass="w-full mb-3">
                    <span class="text-sm">Die Anzahl der aktuellen Fehlermeldungen konnte nicht ermittelt werden.</span>
                </p-message>
            }

            @if (messagesWidget) {
                <mvs-widget [widgetData]="messagesWidget"></mvs-widget>
            }
        </section>

    </div>
}
